<template>
  <div class="w-full h-full flex flex-col p-4 overflow-hidden">
    <!-- Header -->
    <div class="shrink-0 mb-4 text-center">
      <h2 class="text-2xl font-bold text-theme-primary">
        {{ t('gallery.title') }}
      </h2>
      <p class="text-sm text-theme-secondary mt-1">
        {{ t('gallery.count', { count: images.length }) }}
      </p>
    </div>

    <!-- Grid -->
    <div class="flex-1 overflow-y-auto overflow-x-hidden p-[3px]">
      <div class="grid grid-cols-2 md:grid-cols-3 gap-3 pb-4">
        <div
          v-for="(image, index) in images"
          :key="image.publicId"
          @click="$emit('selectImage', index)"
          class="relative aspect-square rounded-lg overflow-hidden cursor-pointer group bg-grid-card hover-ring transition-all duration-200"
        >
          <!-- Image/Video Thumbnail -->
          <img
            :src="getGridThumbnail(image)"
            :alt="image.filename || 'Image'"
            class="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
            loading="lazy"
          />

          <!-- Video indicator badge -->
          <div v-if="image.resourceType === 'video'" class="absolute top-2 left-2 bg-black/60 backdrop-blur-sm text-white p-1.5 rounded flex items-center justify-center">
            <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z"/>
            </svg>
          </div>

          <!-- Hover overlay -->
          <div class="absolute inset-0 bg-black opacity-0 group-hover:bg-black/20 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
            <svg class="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
            </svg>
          </div>

          <!-- Image number badge -->
          <div class="absolute top-2 right-2 bg-black/60 backdrop-blur-sm text-white text-xs font-semibold px-2 py-1 rounded">
            {{ index + 1 }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useLanguage } from '@composables/useLanguage'
import { useCloudinaryTransform } from '@composables/useCloudinaryTransform'

defineProps({
  images: {
    type: Array,
    required: true,
    default: () => []
  }
})

defineEmits(['selectImage'])

const { t } = useLanguage()
const { transformImage } = useCloudinaryTransform()

/**
 * Generate optimized thumbnail URL for grid display
 * Supports both images and videos with proper resource type
 */
const getGridThumbnail = (image) => {
  const cloudName = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME
  const publicId = image.publicId
  const resourceType = image.resourceType || 'image'

  // Grid thumbnails: 400px square, auto quality/format
  // For videos, use video/upload to generate video thumbnail
  return `https://res.cloudinary.com/${cloudName}/${resourceType}/upload/w_400,h_400,c_fill,q_auto,f_auto/${publicId}`
}
</script>

<style scoped>
/* Grid Card Background */
.bg-grid-card {
  background-color: var(--theme-grid-card-bg);
  border: 1px solid var(--theme-grid-card-border);
}

/* Grid Card Hover State */
.group:hover {
  background-color: var(--theme-grid-card-bg-hover);
  border-color: var(--theme-grid-card-border-hover);
}

/* Hover Ring Color */
.hover-ring:hover {
  box-shadow: 0 0 0 2px var(--theme-primary-color);
}

/* Hover Overlay */
.group-hover\:bg-black\/20 {
  background-color: var(--theme-grid-overlay-bg);
}

/* Video Badge Background */
.bg-black\/60 {
  background-color: var(--theme-grid-overlay-bg-hover);
}

/* Image Number Badge */
.absolute.bg-black\/60 {
  background-color: var(--theme-grid-overlay-bg-hover);
}

.text-theme-primary{
  color: var(--color-theme-primary-text);
}

.text-theme-secondary{
  color: var(--color-theme-secondary-text);
}
</style>
